import { IAuthorizationDto } from "./authorization.dto";
import { IConditionModel, ISymbol } from "./builder.dtos";
import { IDisplayItemComponent } from "./display-item-component";
import { IKuikaStyle } from "./kuika-style";
import { INavScreenInfo } from "./nav-screen-info";

export enum EEditabilityType {
  Enabled,
  Disabled,
  Custom
}

export interface IEditability {
  editabilityType: EEditabilityType;
  condition?: IConditionModel;
}

export interface IDisplayItem {
  id: number;
  friendlyName?: string;
  item: IDisplayItemComponent;
  children: IDisplayItem[];
  parentId?: number | undefined;
  styleObj?: IKuikaStyle;
  navScreenInfo?: INavScreenInfo;
  fixedPositionInfo?: IFixedPosition;
  absolutePositionInfo?: IAbsolutePosition;
  authorization?: IAuthorizationDto;
  title?: string;
  expanded?: boolean;
  editability?: IEditability;
  isInteractionDisabled?: boolean;
  isChildrenInteractionDisabled?: boolean;
  designTimeLocked?: boolean;
  designTimeHidden?: boolean;
  /* ── Screen-contract CHANNELS — our own identity, plus three references ──
   *
   * Until now a component carried exactly ONE identity channel: `friendlyName`,
   * a 32-char `[A-Za-z0-9_-]` string that had to encode every reference the
   * deterministic Wirer/Binder needed. Four unrelated meanings were packed into
   * it by prefix — `val-<Field>` (a display value), `<ParameterName>` (a form
   * input), `list-<Alias>` (a repeater), `btn-<Action>` / `row-nav-<Screen>`
   * (an interaction) — and every failure of that overload is on record:
   *
   *   • KOA-18296 — a screen that SHOWS a field and EDITS it produced the same
   *     id twice, because "which slot" and "which field" shared one string. The
   *     `val-` prefix was invented to give the two roles separate key spaces.
   *   • KOA-17779 — `row-nav-HastaDetayMuayeneDuzenleme` (34 chars) clamped to
   *     32 and the button shipped UNWIRED, because an interaction reference was
   *     living inside a length-capped identifier field.
   *   • KOA-17564 — two distinct row navigations collapsed onto one derived id,
   *     because the interaction was DERIVED from the action rather than carried.
   *
   * These fields end the overload: each meaning gets its own channel, so none of
   * them competes with the others for length, uniqueness, or prefix space.
   *
   * `friendlyName` is NOT one of them and is deliberately left alone. The system
   * produces it — derived from the label / content the user actually set, ASCII-
   * folded, capped at 32 because it is emitted downstream as a CODE identifier
   * (`Analyser.cs` builds `{friendlyName}_{rendererName}`). Those constraints are
   * the system's, for the system's purpose, and we stop borrowing the field to
   * carry ours. `contractId` below is our own id in our own namespace, under our
   * own rules.
   *
   * All three are design-time metadata, exactly like `friendlyName` and
   * `designTimeLocked`: no `ISymbol`, no component-definition entry, no runtime
   * expression, and `[KIgnoreCodeGenerationValidation]` on the C# side. A ref
   * NAMES a contract slot; it never evaluates to a value. Resolving a ref into
   * an actual binding or event is the job of the binding/wiring module, which
   * reads these fields and writes `item.properties[].symbol` / `item.events`.
   */
  /**
   * OUR id for this component, in the screen contract's own namespace.
   *
   * Minted by the contract side and stamped onto the component; never derived
   * from a label, a placeholder, or any other user-visible text, so it is
   * wording-, locale- and case-agnostic by construction rather than by a
   * transliteration table. It is not a code identifier and nothing downstream
   * compiles it, so it carries no length cap and needs no ASCII folding — the
   * two constraints that made `friendlyName` the wrong place to keep it.
   *
   * It must be UNIQUE per screen: the binding/wiring module resolves a component
   * by this key, and two components answering to one id means it picks one of
   * them arbitrarily. Enforcement belongs with that module, which is the party
   * that mints them and can say something useful about a collision; until then
   * this is a stated invariant, not a checked one.
   *
   * Distinct from `id` (the numeric instance id the designer allocates, which is
   * per-session tree bookkeeping and means nothing to the contract) and from
   * `friendlyName` (the system's name for the component). A component may carry
   * this without any reference below — a container we need to find again but
   * which owns no data and fires no event.
   */
  contractId?: string;
  /**
   * The screen-contract DATA SLOT this component represents — a value, a record
   * source, or a collection source (`d_<digest>` / `si_<digest>`, minted by the
   * planner's `slot-id.ts`). Set on the component that OWNS the data: the
   * repeater for a collection, the container for a record, the input or label
   * for a value. Absent on pure layout and decoration.
   */
  dataRef?: string;
  /**
   * The FIELD inside the referenced record or collection, by its frozen
   * signature name (`"Date"`, `"Amount"`). Deliberately NOT required at a
   * component's root: it belongs on the leaf that renders one field of a parent
   * `dataRef` — a `TableDefaultColumn`'s cell, a detail card's label. A leaf
   * carrying `fieldRef` inherits its slot from the nearest ancestor `dataRef`,
   * so the pair addresses `(slot, field)` without repeating the digest on every
   * cell.
   */
  fieldRef?: string;
  /**
   * The screen-contract INTERACTION this component triggers (`I<n>`), covering
   * both action invocation and navigation. Set on the component that PRODUCES
   * the event — a button, a row-action button, a menu item — never on its
   * wrapper. This is what replaces deriving a button's identity from the action
   * it happens to fire, which is why two row navigations to different screens
   * can no longer collide.
   */
  interactionRef?: string;
}

export interface IFixedPosition {
  zIndex?: number;
  top?: number;
  left?: number;
  width?: number;
  height?: number;
}

export interface IAbsolutePosition {
  zIndex?: number;
  top?: number;
  left?: number;
  width?: number;
  height?: number;
}

export interface IDataBindingSelect {
  selectPK: string;
  actionDefinitionId: string;
}

export interface IDataBindingValue {
  symbol: ISymbol;
}
